import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import CreateFOOrderForm from '../components/CreateFOOrderForm';
import EditFOOrderModal from '../components/EditFOOrderModal';
import CreateFOCustomerForm from '../components/CreateFOCustomerForm';
import axios from 'axios';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiSearch, FiX, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const FOOrderPage = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showCreateCustomerForm, setShowCreateCustomerForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalOrders, setTotalOrders] = useState(0);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [logisticFilter, setLogisticFilter] = useState('all');
    const [editingField, setEditingField] = useState(null); // { orderId: '...', field: 'trackingNumber' | 'shipCostRMB' | 'logisticAndTracking' }
    const [editValue, setEditValue] = useState('');
    const [editLogistic, setEditLogistic] = useState(''); // For editing logistic when editing both fields
    const [editTrackingNumber, setEditTrackingNumber] = useState(''); // For editing trackingNumber when editing both fields
    const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded order rows
    const [editingItemField, setEditingItemField] = useState(null); // { orderId: '...', itemIndex: 0, field: 'orderDate' | 'doReceiveDate' | 'warehouseRcvDate' }
    const [editItemValue, setEditItemValue] = useState('');
    const [viewCustomerDetails, setViewCustomerDetails] = useState(null); // Full customer details for view modal

    const fetchOrders = async (page, search = '', customerId = 'all', logistic = 'all') => {
        try {
            setLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/foorders?page=${page}&limit=20`;
            
            if (search) apiUrl += `&search=${search}`;
            if (customerId && customerId !== 'all') apiUrl += `&customerId=${customerId}`;
            if (logistic && logistic !== 'all') apiUrl += `&logistic=${encodeURIComponent(logistic)}`;
            
            const token = localStorage.getItem('token');
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setOrders(response.data.data);
                setTotalPages(response.data.pagination.pages);
                setTotalOrders(response.data.pagination.total);
            }
        } catch (err) {
            setError(`Failed to fetch GZ orders: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(currentPage, searchTerm, 'all', logisticFilter);
    }, [currentPage, searchTerm, logisticFilter]);

    // Auto-expand rows where any item has no warehouse receive date; hide order items when all items have warehouse receive date
    useEffect(() => {
        if (orders.length > 0) {
            const newExpanded = new Set(expandedRows);
            let hasChanges = false;
            orders.forEach(order => {
                const hasAnyItemWithoutWarehouseDate = order.items && order.items.length > 0 && order.items.some(item => !item.warehouseRcvDate);
                if (hasAnyItemWithoutWarehouseDate) {
                    if (!newExpanded.has(order._id)) {
                        newExpanded.add(order._id);
                        hasChanges = true;
                    }
                } else if (order.items && order.items.length > 0) {
                    // All items have warehouse receive date → collapse (hide order items)
                    if (newExpanded.has(order._id)) {
                        newExpanded.delete(order._id);
                        hasChanges = true;
                    }
                }
            });
            if (hasChanges) {
                setExpandedRows(newExpanded);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orders]);

    const handleCreateSuccess = (newOrder) => {
        setShowCreateForm(false);
        fetchOrders(currentPage, searchTerm, 'all', logisticFilter);
    };

    const handleCreateCustomerSuccess = async (newCustomer) => {
        setShowCreateCustomerForm(false);
    };

    const handleView = async (order) => {
        setSelectedOrder(order);
        setShowViewModal(true);
        
        // Fetch full customer details if customer exists
        if (order.customer) {
            try {
                const customerId = typeof order.customer === 'object' ? order.customer._id : order.customer;
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/focustomers/${customerId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (response.data.success) {
                    setViewCustomerDetails(response.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch customer details:', err);
                // Fallback to basic customer data if available
                if (typeof order.customer === 'object') {
                    setViewCustomerDetails(order.customer);
                }
            }
        } else {
            setViewCustomerDetails(null);
        }
    };

    const handleEdit = (order) => {
        setSelectedOrder(order);
        setShowEditModal(true);
    };

    const handleEditSuccess = (updatedOrder) => {
        setShowEditModal(false);
        setSelectedOrder(null);
        fetchOrders(currentPage, searchTerm, 'all', logisticFilter);
    };

    const handleDelete = (order) => {
        setSelectedOrder(order);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/foorders/${selectedOrder._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            setShowDeleteConfirm(false);
            setSelectedOrder(null);
            fetchOrders(currentPage, searchTerm, 'all', logisticFilter);
        } catch (err) {
            setError(`Failed to delete GZ order: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setLogisticFilter('all');
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-SG', { year: 'numeric', month: '2-digit', day: '2-digit' });
    };

    // Formatting helper functions
    const formatRate = (value) => {
        if (!value && value !== 0) return 'N/A';
        return parseFloat(value).toFixed(2);
    };

    const formatRMB = (value) => {
        if (!value && value !== 0) return 'N/A';
        return `RMB ${parseFloat(value).toFixed(2)}`;
    };

    const formatSGD = (value) => {
        if (!value && value !== 0) return 'N/A';
        return `$${parseFloat(value).toFixed(2)}`;
    };

    const formatMargin = (value) => {
        if (!value && value !== 0) return 'N/A';
        return `${parseFloat(value).toFixed(2)}%`;
    };

    const formatAmount = (amount) => {
        if (!amount && amount !== 0) return 'N/A';
        return `$${amount.toFixed(2)}`;
    };

    // Calculate Total Sales for an order (totalSellingPrice - discount + deliveryFees - sum of creditNotes.amountSGD)
    const calculateTotalSales = (order) => {
        const totalSellingPrice = (order.items && order.items.length > 0)
            ? order.items.reduce((sum, item) => sum + (parseFloat(item.sellingPrice) || 0), 0)
            : 0;
        const discount = parseFloat(order.discount) || 0;
        const deliveryFees = parseFloat(order.deliveryFees) || 0;
        const creditNoteTotal = (order.creditNotes && Array.isArray(order.creditNotes))
            ? order.creditNotes.reduce((sum, cn) => sum + (parseFloat(cn.amountSGD) || 0), 0)
            : 0;
        return totalSellingPrice - discount + deliveryFees - creditNoteTotal;
    };

    // Calculate Duration: customerReceiveDate - earliest orderDate in items array
    const calculateDuration = (order) => {
        if (!order.customerReceiveDate) return 'N/A';
        if (!order.items || order.items.length === 0) return 'N/A';
        
        // Find earliest orderDate in items
        const orderDates = order.items
            .map(item => item.orderDate)
            .filter(date => date) // Filter out null/undefined dates
            .map(date => new Date(date));
        
        if (orderDates.length === 0) return 'N/A';
        
        const earliestOrderDate = new Date(Math.min(...orderDates));
        const customerReceiveDate = new Date(order.customerReceiveDate);
        
        // Calculate difference in days
        const diffTime = customerReceiveDate - earliestOrderDate;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return `${diffDays} days`;
    };

    // Update order field
    const updateOrderField = async (orderId, field, value) => {
        try {
            const token = localStorage.getItem('token');
            const updateData = { [field]: value };
            
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/foorders/${orderId}`,
                updateData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            // Refresh orders list
            fetchOrders(currentPage, searchTerm, 'all', logisticFilter);
            setEditingField(null);
            setEditValue('');
        } catch (err) {
            setError(`Failed to update ${field}: ${err.response?.data?.error || err.message}`);
            setEditingField(null);
            setEditValue('');
        }
    };

    // Handle shippingBillDate checkbox change
    const handleShippingBillDateChange = async (order) => {
        const currentDate = order.shippingBillDate;
        let newDate;
        
        if (currentDate) {
            // If date exists, clear it (unchecking)
            newDate = null; // Use null instead of undefined for proper API handling
            // Auto-expand the row when unchecked
            const newExpanded = new Set(expandedRows);
            newExpanded.add(order._id);
            setExpandedRows(newExpanded);
        } else {
            // If no date, set to today (checking)
            const today = new Date();
            newDate = today; // Send as Date object, API will handle it
            // Collapse the row when checked (if it was auto-expanded)
            const newExpanded = new Set(expandedRows);
            newExpanded.delete(order._id);
            setExpandedRows(newExpanded);
        }
        
        await updateOrderField(order._id, 'shippingBillDate', newDate);
    };

    // Handle inline edit start
    const handleEditStart = (order, field) => {
        setEditingField({ orderId: order._id, field });
        if (field === 'trackingNumber') {
            setEditValue(order.trackingNumber || '');
        } else if (field === 'logisticAndTracking') {
            setEditLogistic(order.logistic || '');
            setEditTrackingNumber(order.trackingNumber || '');
        } else if (field === 'shipCostRMB') {
            setEditValue(order.shipCostRMB || '');
        } else if (field === 'shippedOutDate') {
            // Format date for input (YYYY-MM-DD)
            if (order.shippedOutDate) {
                const date = new Date(order.shippedOutDate);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                setEditValue(`${year}-${month}-${day}`);
            } else {
                setEditValue('');
            }
        } else if (field === 'customerReceiveDate') {
            // Format date for input (YYYY-MM-DD)
            if (order.customerReceiveDate) {
                const date = new Date(order.customerReceiveDate);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                setEditValue(`${year}-${month}-${day}`);
            } else {
                setEditValue('');
            }
        }
    };

    // Handle inline edit save
    const handleEditSave = async (orderId, field) => {
        if (field === 'logisticAndTracking') {
            // Save both fields at once
            try {
                const token = localStorage.getItem('token');
                const updateData = {
                    logistic: editLogistic.trim() || undefined,
                    trackingNumber: editTrackingNumber.trim() || undefined
                };
                
                await axios.put(
                    `${process.env.REACT_APP_API_URL}/api/foorders/${orderId}`,
                    updateData,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                
                // Refresh orders list
                fetchOrders(currentPage, searchTerm, 'all', logisticFilter);
                setEditingField(null);
                setEditLogistic('');
                setEditTrackingNumber('');
            } catch (err) {
                setError(`Failed to update logistic/tracking number: ${err.response?.data?.error || err.message}`);
                setEditingField(null);
                setEditLogistic('');
                setEditTrackingNumber('');
            }
        } else {
            let value = editValue;
            
            if (field === 'shipCostRMB') {
                value = editValue ? parseFloat(editValue) : undefined;
            } else if (field === 'trackingNumber') {
                value = editValue.trim() || undefined;
            } else if (field === 'logistic') {
                value = editValue.trim() || undefined;
            } else if (field === 'shippedOutDate') {
                // Convert date string to Date object or undefined
                if (editValue && editValue.trim()) {
                    value = new Date(editValue);
                } else {
                    value = undefined;
                }
            } else if (field === 'customerReceiveDate') {
                // Convert date string to Date object or undefined
                if (editValue && editValue.trim()) {
                    value = new Date(editValue);
                } else {
                    value = undefined;
                }
            }
            
            updateOrderField(orderId, field, value);
        }
    };

    // Handle inline edit cancel
    const handleEditCancel = () => {
        setEditingField(null);
        setEditValue('');
        setEditLogistic('');
        setEditTrackingNumber('');
    };

    // Toggle row expansion (only when all items have warehouse receive date; otherwise items are always shown)
    const toggleRowExpansion = (orderId) => {
        const order = orders.find(o => o._id === orderId);
        const allItemsHaveWarehouseDate = order?.items?.length && order.items.every(item => item.warehouseRcvDate);
        if (order && allItemsHaveWarehouseDate) {
            const newExpanded = new Set(expandedRows);
            if (newExpanded.has(orderId)) {
                newExpanded.delete(orderId);
            } else {
                newExpanded.add(orderId);
            }
            setExpandedRows(newExpanded);
        }
    };

    // Handle item field edit start
    const handleItemEditStart = (orderId, itemIndex, field, currentValue) => {
        setEditingItemField({ orderId, itemIndex, field });
        // Format date for input (YYYY-MM-DD)
        if (currentValue) {
            const date = new Date(currentValue);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            setEditItemValue(`${year}-${month}-${day}`);
        } else {
            setEditItemValue('');
        }
    };

    // Handle item field edit save
    const handleItemEditSave = async (orderId, itemIndex, field) => {
        try {
            const token = localStorage.getItem('token');
            // Get the order to find the item
            const order = orders.find(o => o._id === orderId);
            if (!order || !order.items || !order.items[itemIndex]) {
                setError('Order or item not found');
                setEditingItemField(null);
                setEditItemValue('');
                return;
            }

            // Update the item in the order
            const updatedItems = [...order.items];
            const item = updatedItems[itemIndex];
            
            // Convert date string to Date object or undefined
            let dateValue = undefined;
            if (editItemValue && editItemValue.trim()) {
                dateValue = new Date(editItemValue);
            }

            // Update the specific field
            if (field === 'orderDate') {
                item.orderDate = dateValue;
            } else if (field === 'doReceiveDate') {
                item.doReceiveDate = dateValue;
            } else if (field === 'warehouseRcvDate') {
                item.warehouseRcvDate = dateValue;
            }

            // Send update to backend
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/foorders/${orderId}`,
                { items: updatedItems },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );

            // Refresh orders list
            fetchOrders(currentPage, searchTerm, 'all', logisticFilter);
            setEditingItemField(null);
            setEditItemValue('');
        } catch (err) {
            setError(`Failed to update item ${field}: ${err.response?.data?.error || err.message}`);
            setEditingItemField(null);
            setEditItemValue('');
        }
    };

    // Handle item field edit cancel
    const handleItemEditCancel = () => {
        setEditingItemField(null);
        setEditItemValue('');
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">GZ Orders</h1>
                        <p className="text-gray-600">Total: {totalOrders} orders</p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowCreateCustomerForm(true)}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                        >
                            <FiPlus className="mr-2" />
                            Create New Customer
                        </button>
                        <button
                            onClick={() => setShowCreateForm(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                        >
                            <FiPlus className="mr-2" />
                            Create New Order
                        </button>
                    </div>
                </div>

                {/* Search and Filter Bar */}
                <div className="mb-6">
                    <div className="flex gap-2 mb-2">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                placeholder="Search by Purchase Code, Web Order ID, or Tracking Number..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <FiSearch className="h-5 w-5 text-gray-400" />
                            </div>
                        </div>
                        <select
                            value={logisticFilter}
                            onChange={(e) => {
                                setLogisticFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="all">All Logistics</option>
                            <option value="Ken">Ken</option>
                            <option value="LHZ">LHZ</option>
                        </select>
                        <button
                            onClick={handleSearch}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                        >
                            <FiSearch className="mr-2" />
                            Search
                        </button>
                        <button
                            onClick={handleClear}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center"
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {/* Create Form Modal */}
                {showCreateForm && (
                    <CreateFOOrderForm
                        onSuccess={handleCreateSuccess}
                        onCancel={() => setShowCreateForm(false)}
                    />
                )}

                {/* Create Customer Form Modal */}
                {showCreateCustomerForm && (
                    <CreateFOCustomerForm
                        onSuccess={handleCreateCustomerSuccess}
                        onCancel={() => setShowCreateCustomerForm(false)}
                    />
                )}

                {/* Edit Modal */}
                {showEditModal && selectedOrder && (
                    <EditFOOrderModal
                        order={selectedOrder}
                        onSuccess={handleEditSuccess}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedOrder(null);
                        }}
                    />
                )}

                {/* View Modal */}
                {showViewModal && selectedOrder && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border max-w-6xl w-full shadow-lg rounded-md bg-white">
                            <div className="flex justify-between items-center border-b pb-4 mb-4">
                                <h3 className="text-xl font-medium">
                                    GZ Order Details - <span className="ml-2 text-blue-700">{selectedOrder.purchaseCode}</span>
                                </h3>
                                <div className="flex items-center gap-4">
                                    <div className="text-right">
                                        <span className="text-sm text-gray-600">Total Profit: </span>
                                        <span className="text-lg font-semibold text-green-600">
                                            {formatSGD(selectedOrder.totalProfit !== undefined ? selectedOrder.totalProfit : 0)}
                                        </span>
                                    </div>
                                    <button 
                                        onClick={() => { 
                                            setShowViewModal(false); 
                                            setSelectedOrder(null);
                                            setViewCustomerDetails(null);
                                        }} 
                                        className="text-gray-500 hover:text-gray-700"
                                    >
                                        <FiX className="w-6 h-6" />
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-6">
                                {/* Order Information Card */}
                                <section>
                                    <h4 className="text-lg font-medium mb-3">Order Information</h4>
                                    <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                                        {/* Customer + Contact for this order (same preview as Create/Edit: labels with count, address fallback last, measurements/remarks below) */}
                                        {viewCustomerDetails && (
                                            <div className="bg-white p-4 rounded-md border border-gray-200">
                                                <h5 className="text-md font-semibold mb-2 text-gray-700">Customer & contact for this order</h5>
                                                <div className="space-y-2">
                                                    <div className="font-semibold text-gray-900">
                                                        {viewCustomerDetails.customerID} - {viewCustomerDetails.Name}
                                                    </div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 text-sm text-gray-700">
                                                        <div>
                                                            <span className="font-medium">Email:</span>{' '}
                                                            {selectedOrder.orderEmail || (viewCustomerDetails.email && viewCustomerDetails.email[0]) || 'N/A'}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Address:</span>{' '}
                                                            {selectedOrder.orderAddress || (viewCustomerDetails.Address && viewCustomerDetails.Address.length > 0 ? viewCustomerDetails.Address[viewCustomerDetails.Address.length - 1] : null) || 'N/A'}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Phone:</span>{' '}
                                                            {selectedOrder.orderPhone || (viewCustomerDetails.Phone && viewCustomerDetails.Phone[0]) || 'N/A'}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">WhatsApp:</span>{' '}
                                                            {selectedOrder.orderWhatsapp || (viewCustomerDetails.Whatsapp && viewCustomerDetails.Whatsapp[0]) || 'N/A'}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Telegram:</span>{' '}
                                                            {selectedOrder.orderTelegram && (selectedOrder.orderTelegram.username || selectedOrder.orderTelegram.tgNumber)
                                                                ? [selectedOrder.orderTelegram.username, selectedOrder.orderTelegram.tgNumber].filter(Boolean).join(' – ')
                                                                : (viewCustomerDetails.Telegram && viewCustomerDetails.Telegram[0]
                                                                    ? (typeof viewCustomerDetails.Telegram[0] === 'string'
                                                                        ? viewCustomerDetails.Telegram[0]
                                                                        : [viewCustomerDetails.Telegram[0]?.username, viewCustomerDetails.Telegram[0]?.tgNumber].filter(Boolean).join(' – '))
                                                                    : 'N/A')}
                                                        </div>
                                                    </div>
                                                    {/* Measurements / Remarks (small font) - only if at least one value present */}
                                                    {(() => {
                                                        const c = viewCustomerDetails;
                                                        const hasHeight = c.Height != null && c.Height !== '';
                                                        const hasWeight = c.Weight != null && c.Weight !== '';
                                                        const hasWaist = c.waist != null && c.waist !== '';
                                                        const hasWrist = c.wrist != null && c.wrist !== '';
                                                        const hasShoeSize = c.shoeSize != null && c.shoeSize !== '';
                                                        const hasRingSize = c.ringSize != null && c.ringSize !== '';
                                                        const hasRemarks = c.remarks && String(c.remarks).trim() !== '';
                                                        const parts = [];
                                                        if (hasHeight) parts.push(`Height: ${Number(c.Height).toFixed(0)} cm`);
                                                        if (hasWeight) parts.push(`Weight: ${Number(c.Weight).toFixed(2)} kg`);
                                                        if (hasWaist) parts.push(`Waist: ${Number(c.waist).toFixed(2)} cm`);
                                                        if (hasWrist) parts.push(`Wrist: ${Number(c.wrist).toFixed(2)} cm`);
                                                        if (hasShoeSize) parts.push(`Shoe size: ${Number(c.shoeSize).toFixed(2)}`);
                                                        if (hasRingSize) parts.push(`Ring size: ${Number(c.ringSize).toFixed(2)}`);
                                                        const hasAnyMeasurement = parts.length > 0;
                                                        if (!hasAnyMeasurement && !hasRemarks) return null;
                                                        return (
                                                            <div className="mt-3 text-xs text-gray-600 border-t border-gray-100 pt-2 col-span-full">
                                                                {hasAnyMeasurement && (
                                                                    <div className="mb-1">{parts.join(', ')}</div>
                                                                )}
                                                                {hasRemarks && (
                                                                    <div><span className="font-medium">Remarks:</span> {c.remarks}</div>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Order Details Row 1 */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {selectedOrder.webOrderID && (
                                                <div>
                                                    <span className="text-gray-600 text-sm">Web Order ID:</span>
                                                    <span className="ml-2 text-blue-700">{selectedOrder.webOrderID}</span>
                                                </div>
                                            )}
                                            {selectedOrder.rate !== undefined && (
                                                <div>
                                                    <span className="text-gray-600 text-sm">SGD/RMB Rate:</span>
                                                    <span className="ml-2 text-blue-700">{formatRate(selectedOrder.rate)}</span>
                                                </div>
                                            )}
                                            {selectedOrder.deliveryFees !== undefined && (
                                                <div>
                                                    <span className="text-gray-600 text-sm">Delivery Fees:</span>
                                                    <span className="ml-2 text-blue-700">{formatSGD(selectedOrder.deliveryFees)}</span>
                                                </div>
                                            )}
                                            {selectedOrder.discount !== undefined && (
                                                <div>
                                                    <span className="text-gray-600 text-sm">Discount:</span>
                                                    <span className="ml-2 text-blue-700">{formatSGD(selectedOrder.discount)}</span>
                                                </div>
                                            )}
                                            {selectedOrder.paymentMethod && (
                                                <div>
                                                    <span className="text-gray-600 text-sm">Payment Method:</span>
                                                    <span className="ml-2 text-blue-700">{selectedOrder.paymentMethod}</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Order Details Row 2 */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div>
                                                <span className="text-gray-600 text-sm">Total Sales:</span>
                                                <span className="ml-2 text-blue-700 font-semibold">{formatSGD((selectedOrder.totalSales !== undefined && selectedOrder.totalSales !== null) ? selectedOrder.totalSales : calculateTotalSales(selectedOrder))}</span>
                                            </div>
                                            {selectedOrder.shipCostSGD !== undefined && (
                                                <div>
                                                    <span className="text-gray-600 text-sm">Ship Cost SGD:</span>
                                                    <span className="ml-2 text-blue-700">{formatSGD(selectedOrder.shipCostSGD)}</span>
                                                </div>
                                            )}
                                            {selectedOrder.totalProfit !== undefined && (
                                                <div>
                                                    <span className="text-gray-600 text-sm">Total Profit:</span>
                                                    <span className="ml-2 text-green-600 font-semibold">{formatSGD(selectedOrder.totalProfit)}</span>
                                                </div>
                                            )}
                                            {selectedOrder.margin !== undefined && (
                                                <div>
                                                    <span className="text-gray-600 text-sm">Margin:</span>
                                                    <span className="ml-2 text-blue-700">{formatMargin(selectedOrder.margin)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </section>

                                {/* Credit Notes - only if order has credit notes */}
                                {selectedOrder.creditNotes && Array.isArray(selectedOrder.creditNotes) && selectedOrder.creditNotes.length > 0 && (
                                    <section>
                                        <h4 className="text-lg font-medium mb-3">Credit Notes</h4>
                                        <div className="bg-red-50 p-4 rounded-lg space-y-4">
                                            {(selectedOrder.creditNotes || []).map((cn, index) => (
                                                <div key={index} className="bg-red-50 rounded-md">
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                                        <span className="text-gray-700"><span className="font-medium">Type:</span> {cn.type === 'aftersalesservice' ? 'After Sales Service' : (cn.type || 'N/A')}</span>
                                                        <span className="text-gray-700"><span className="font-medium">Amount:</span> {formatSGD(cn.amountSGD != null ? cn.amountSGD : 0)}</span>
                                                    </div>
                                                    <div className="mt-1 text-sm text-gray-700">
                                                        <span className="font-medium">Remarks:</span> {cn.remarks || 'N/A'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {/* Order Items */}
                                {selectedOrder.items && selectedOrder.items.length > 0 && (
                                    <section>
                                        <h4 className="text-lg font-medium mb-3">Order Items ({selectedOrder.items.length})</h4>
                                        <div className="bg-yellow-50 p-4 rounded-lg">
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-100">
                                                        <tr>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Ordering Code</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Category</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Subcategory</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Brand</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Order Date</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Selling Price</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Cost RMB</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Supplier</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Cost SGD</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Item Profit SGD</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Remarks</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-yellow-50 divide-y divide-gray-200">
                                                        {selectedOrder.items.map((item, index) => (
                                                            <tr key={index}>
                                                                <td className="px-3 py-2 text-sm">{item.orderingcode || 'N/A'}</td>
                                                                <td className="px-3 py-2 text-sm">
                                                                    {item.category ? (item.category.category || 'N/A') : 'N/A'}
                                                                </td>
                                                                <td className="px-3 py-2 text-sm">{item.subcategory || 'N/A'}</td>
                                                                <td className="px-3 py-2 text-sm">
                                                                    {item.brand ? (item.brand.Brand || 'N/A') : 'N/A'}
                                                                </td>
                                                                <td className="px-3 py-2 text-sm">{formatDate(item.orderDate)}</td>
                                                                <td className="px-3 py-2 text-sm">{formatSGD(item.sellingPrice)}</td>
                                                                <td className="px-3 py-2 text-sm">{formatRMB(item.costRMB)}</td>
                                                                <td className="px-3 py-2 text-sm">
                                                                    {item.supplier ? (
                                                                        item.supplier.supplierCode + (item.supplier.supplierName ? ` - ${item.supplier.supplierName}` : '')
                                                                    ) : 'N/A'}
                                                                </td>
                                                                <td className="px-3 py-2 text-sm">{formatSGD(item.costInSGD)}</td>
                                                                <td className="px-3 py-2 text-sm font-semibold text-green-700">{formatSGD(item.itemProfitSGD)}</td>
                                                                <td className="px-3 py-2 text-sm">{item.remarks || 'N/A'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </section>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && selectedOrder && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
                            <p className="mb-6">Are you sure you want to delete order "{selectedOrder.purchaseCode}"?</p>
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => { setShowDeleteConfirm(false); setSelectedOrder(null); }}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Table */}
                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                        <thead className="bg-black">
                            <tr>
                                <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                                    Purchase Code
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                                    Customer
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                                    Logistic - Tracking Number
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                                    Shipping Bill Date
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                                    Ship Cost RMB
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                                    Shipped Out Date
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                                    Customer Receive Date
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                                    Duration
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                                    Total Sales
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                                    Total Profit
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                                    Margin
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="12" className="px-3 py-2 text-center text-xs">
                                        Loading...
                                    </td>
                                </tr>
                            ) : orders.length === 0 ? (
                                <tr>
                                    <td colSpan="12" className="px-3 py-2 text-center text-xs">
                                        No GZ orders found
                                    </td>
                                </tr>
                            ) : (
                                orders.map((order) => {
                                    const totalSales = (order.totalSales !== undefined && order.totalSales !== null) ? order.totalSales : calculateTotalSales(order);
                                    const totalProfit = order.totalProfit !== undefined ? order.totalProfit : 0;
                                    const margin = order.margin !== undefined ? order.margin : 0;
                                    
                                    return (
                                        <React.Fragment key={order._id}>
                                            <tr className="hover:bg-gray-50">
                                                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium">
                                                    {order.purchaseCode}
                                                </td>
                                                <td className="px-3 py-2 text-xs">
                                                    {order.customer ? `${order.customer.customerID} - ${order.customer.Name}` : 'N/A'}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs">
                                                    {editingField?.orderId === order._id && editingField?.field === 'logisticAndTracking' ? (
                                                        <div className="flex items-center gap-2">
                                                            <select
                                                                value={editLogistic}
                                                                onChange={async (e) => {
                                                                    const newLogistic = e.target.value;
                                                                    setEditLogistic(newLogistic);
                                                                    // Save immediately when logistic changes
                                                                    // Use editTrackingNumber if it has a value, otherwise use the order's current trackingNumber
                                                                    const trackingToSave = editTrackingNumber.trim() || order.trackingNumber || '';
                                                                    try {
                                                                        const token = localStorage.getItem('token');
                                                                        const updateData = {
                                                                            logistic: newLogistic.trim() || undefined,
                                                                            trackingNumber: trackingToSave.trim() || undefined
                                                                        };
                                                                        
                                                                        const response = await axios.put(
                                                                            `${process.env.REACT_APP_API_URL}/api/foorders/${order._id}`,
                                                                            updateData,
                                                                            {
                                                                                headers: {
                                                                                    'Authorization': `Bearer ${token}`,
                                                                                    'ngrok-skip-browser-warning': 'true'
                                                                                }
                                                                            }
                                                                        );
                                                                        
                                                                        // Only refresh if the save was successful
                                                                        if (response.data.success) {
                                                                            // Update local state to reflect the saved value
                                                                            if (!editTrackingNumber.trim()) {
                                                                                setEditTrackingNumber(order.trackingNumber || '');
                                                                            }
                                                                            // Refresh orders list but keep editing mode open
                                                                            await fetchOrders(currentPage, searchTerm, 'all', logisticFilter);
                                                                        }
                                                                    } catch (err) {
                                                                        setError(`Failed to update logistic: ${err.response?.data?.error || err.message}`);
                                                                    }
                                                                }}
                                                                onKeyPress={(e) => {
                                                                    if (e.key === 'Escape') {
                                                                        handleEditCancel();
                                                                    }
                                                                }}
                                                                className="px-2 py-1 text-xs border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                            >
                                                                <option value="">Select Logistic</option>
                                                                <option value="Ken">Ken</option>
                                                                <option value="LHZ">LHZ</option>
                                                            </select>
                                                            <span className="text-gray-500">-</span>
                                                            <input
                                                                type="text"
                                                                value={editTrackingNumber}
                                                                onChange={(e) => setEditTrackingNumber(e.target.value)}
                                                                onBlur={async () => {
                                                                    // Save both fields when tracking number loses focus
                                                                    try {
                                                                        const token = localStorage.getItem('token');
                                                                        const updateData = {
                                                                            logistic: editLogistic.trim() || undefined,
                                                                            trackingNumber: editTrackingNumber.trim() || undefined
                                                                        };
                                                                        
                                                                        await axios.put(
                                                                            `${process.env.REACT_APP_API_URL}/api/foorders/${order._id}`,
                                                                            updateData,
                                                                            {
                                                                                headers: {
                                                                                    'Authorization': `Bearer ${token}`,
                                                                                    'ngrok-skip-browser-warning': 'true'
                                                                                }
                                                                            }
                                                                        );
                                                                        
                                                                        // Refresh and close editing mode
                                                                        await fetchOrders(currentPage, searchTerm, 'all', logisticFilter);
                                                                        setEditingField(null);
                                                                        setEditLogistic('');
                                                                        setEditTrackingNumber('');
                                                                    } catch (err) {
                                                                        setError(`Failed to update tracking number: ${err.response?.data?.error || err.message}`);
                                                                    }
                                                                }}
                                                                onKeyPress={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        handleEditSave(order._id, 'logisticAndTracking');
                                                                    } else if (e.key === 'Escape') {
                                                                        handleEditCancel();
                                                                    }
                                                                }}
                                                                className="px-2 py-1 text-xs border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                placeholder="Tracking Number"
                                                                autoFocus
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            onClick={() => {
                                                                // Start editing both fields
                                                                handleEditStart(order, 'logisticAndTracking');
                                                            }}
                                                            className={`cursor-pointer hover:bg-blue-50 px-2 py-1 rounded ${!order.logistic || !order.trackingNumber ? 'text-red-600' : ''}`}
                                                            title="Click to edit logistic or tracking number"
                                                        >
                                                            {order.logistic ? `${order.logistic} - ${order.trackingNumber || 'N/A'}` : (order.trackingNumber || 'N/A')}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs">
                                                    <div className="flex items-center gap-1">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!order.shippingBillDate}
                                                            onChange={() => handleShippingBillDateChange(order)}
                                                            className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                            title="Shipping Bill Date"
                                                        />
                                                        {order.shippingBillDate && (
                                                            <span className="text-xs text-gray-600">
                                                                {formatDate(order.shippingBillDate)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs">
                                                    {editingField?.orderId === order._id && editingField?.field === 'shipCostRMB' ? (
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                onBlur={() => handleEditSave(order._id, 'shipCostRMB')}
                                                                onKeyPress={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        handleEditSave(order._id, 'shipCostRMB');
                                                                    } else if (e.key === 'Escape') {
                                                                        handleEditCancel();
                                                                    }
                                                                }}
                                                                className="w-full px-2 py-1 text-xs border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                autoFocus
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            onClick={() => handleEditStart(order, 'shipCostRMB')}
                                                            className={`cursor-pointer hover:bg-blue-50 px-2 py-1 rounded ${!order.shippingBillDate ? 'text-red-600' : ''}`}
                                                            title="Click to edit"
                                                        >
                                                            {formatRMB(order.shipCostRMB)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs">
                                                    {editingField?.orderId === order._id && editingField?.field === 'shippedOutDate' ? (
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="date"
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                onBlur={() => handleEditSave(order._id, 'shippedOutDate')}
                                                                onKeyPress={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        handleEditSave(order._id, 'shippedOutDate');
                                                                    } else if (e.key === 'Escape') {
                                                                        handleEditCancel();
                                                                    }
                                                                }}
                                                                className="w-full px-2 py-1 text-xs border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                autoFocus
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            onClick={() => handleEditStart(order, 'shippedOutDate')}
                                                            className={`cursor-pointer hover:bg-blue-50 px-2 py-1 rounded ${!order.shippedOutDate ? 'text-red-600' : ''}`}
                                                            title="Click to edit"
                                                        >
                                                            {formatDate(order.shippedOutDate)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs">
                                                    {editingField?.orderId === order._id && editingField?.field === 'customerReceiveDate' ? (
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="date"
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                onBlur={() => handleEditSave(order._id, 'customerReceiveDate')}
                                                                onKeyPress={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        handleEditSave(order._id, 'customerReceiveDate');
                                                                    } else if (e.key === 'Escape') {
                                                                        handleEditCancel();
                                                                    }
                                                                }}
                                                                className="w-full px-2 py-1 text-xs border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                autoFocus
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            onClick={() => handleEditStart(order, 'customerReceiveDate')}
                                                            className={`cursor-pointer hover:bg-blue-50 px-2 py-1 rounded ${!order.customerReceiveDate ? 'text-red-600' : ''}`}
                                                            title="Click to edit"
                                                        >
                                                            {formatDate(order.customerReceiveDate)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs">
                                                    {calculateDuration(order)}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs font-semibold">
                                                    {formatSGD(totalSales)}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs font-semibold text-green-600">
                                                    {formatSGD(totalProfit)}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-xs font-semibold">
                                                    {formatMargin(margin)}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium space-x-1">
                                                    {(() => {
                                                        const hasAnyItemWithoutWarehouseDate = order.items && order.items.length > 0 && order.items.some(item => !item.warehouseRcvDate);
                                                        const allItemsHaveWarehouseDate = order.items && order.items.length > 0 && order.items.every(item => item.warehouseRcvDate);
                                                        if (allItemsHaveWarehouseDate) {
                                                            return (
                                                                <button
                                                                    onClick={() => toggleRowExpansion(order._id)}
                                                                    className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full inline-flex items-center"
                                                                    title={expandedRows.has(order._id) ? "Collapse" : "Expand"}
                                                                >
                                                                    {expandedRows.has(order._id) ? (
                                                                        <FiChevronUp className="w-3 h-3" />
                                                                    ) : (
                                                                        <FiChevronDown className="w-3 h-3" />
                                                                    )}
                                                                </button>
                                                            );
                                                        }
                                                        if (hasAnyItemWithoutWarehouseDate) {
                                                            return (
                                                                <div className="p-1 text-gray-400 inline-flex items-center" title="Auto-expanded (pending warehouse receive dates)">
                                                                    <FiChevronUp className="w-3 h-3" />
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                    <button
                                                        onClick={() => handleView(order)}
                                                        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center"
                                                        title="View Details"
                                                    >
                                                        <FiEye className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(order)}
                                                        className="p-1 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center"
                                                        title="Edit Order"
                                                    >
                                                        <FiEdit2 className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(order)}
                                                        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center"
                                                        title="Delete Order"
                                                    >
                                                        <FiTrash2 className="w-3 h-3" />
                                                    </button>
                                                </td>
                                            </tr>
                                            {((expandedRows.has(order._id) || (order.items && order.items.some(item => !item.warehouseRcvDate))) && order.items && order.items.length > 0) && (
                                                <tr>
                                                    <td colSpan="12" className="px-3 py-2">
                                                        <div className="overflow-x-auto bg-yellow-50 rounded-lg p-3" style={{ width: '70%' }}>
                                                            <table className="min-w-full divide-y divide-gray-200 text-xs">
                                                                <thead className="bg-yellow-100">
                                                                    <tr>
                                                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">Ordering Code</th>
                                                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">Item</th>
                                                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">Order Date</th>
                                                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">DO Receive Date</th>
                                                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">Warehouse Receive Date</th>
                                                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">Selling Price</th>
                                                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">Cost RMB</th>
                                                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">Item Profit SGD</th>
                                                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">Remarks</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="bg-yellow-50 divide-y divide-gray-200">
                                                                    {order.items.map((item, itemIndex) => {
                                                                        const itemDisplay = `${item.brand ? (item.brand.Brand || '') : ''}${item.brand && item.subcategory ? ' - ' : ''}${item.subcategory || ''}`;
                                                                        const isEditingOrderDate = editingItemField?.orderId === order._id && editingItemField?.itemIndex === itemIndex && editingItemField?.field === 'orderDate';
                                                                        const isEditingDoReceiveDate = editingItemField?.orderId === order._id && editingItemField?.itemIndex === itemIndex && editingItemField?.field === 'doReceiveDate';
                                                                        const isEditingWarehouseRcvDate = editingItemField?.orderId === order._id && editingItemField?.itemIndex === itemIndex && editingItemField?.field === 'warehouseRcvDate';
                                                                        
                                                                        return (
                                                                            <tr key={itemIndex}>
                                                                                <td className="px-2 py-1 text-xs">
                                                                                    {item.orderingcode || ''}
                                                                                </td>
                                                                                <td className="px-2 py-1 text-xs">
                                                                                    {itemDisplay || 'N/A'}
                                                                                </td>
                                                                                <td className="px-2 py-1 text-xs">
                                                                                    {isEditingOrderDate ? (
                                                                                        <input
                                                                                            type="date"
                                                                                            value={editItemValue}
                                                                                            onChange={(e) => setEditItemValue(e.target.value)}
                                                                                            onBlur={() => handleItemEditSave(order._id, itemIndex, 'orderDate')}
                                                                                            onKeyPress={(e) => {
                                                                                                if (e.key === 'Enter') {
                                                                                                    handleItemEditSave(order._id, itemIndex, 'orderDate');
                                                                                                } else if (e.key === 'Escape') {
                                                                                                    handleItemEditCancel();
                                                                                                }
                                                                                            }}
                                                                                            className="w-full px-1 py-0.5 text-xs border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                                            autoFocus
                                                                                        />
                                                                                    ) : (
                                                                                        <div
                                                                                            onClick={() => handleItemEditStart(order._id, itemIndex, 'orderDate', item.orderDate)}
                                                                                            className="cursor-pointer hover:bg-blue-50 px-1 py-0.5 rounded"
                                                                                            title="Click to edit"
                                                                                        >
                                                                                            {formatDate(item.orderDate)}
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-2 py-1 text-xs">
                                                                                    {isEditingDoReceiveDate ? (
                                                                                        <input
                                                                                            type="date"
                                                                                            value={editItemValue}
                                                                                            onChange={(e) => setEditItemValue(e.target.value)}
                                                                                            onBlur={() => handleItemEditSave(order._id, itemIndex, 'doReceiveDate')}
                                                                                            onKeyPress={(e) => {
                                                                                                if (e.key === 'Enter') {
                                                                                                    handleItemEditSave(order._id, itemIndex, 'doReceiveDate');
                                                                                                } else if (e.key === 'Escape') {
                                                                                                    handleItemEditCancel();
                                                                                                }
                                                                                            }}
                                                                                            className="w-full px-1 py-0.5 text-xs border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                                            autoFocus
                                                                                        />
                                                                                    ) : (
                                                                                        <div
                                                                                            onClick={() => handleItemEditStart(order._id, itemIndex, 'doReceiveDate', item.doReceiveDate)}
                                                                                            className={`cursor-pointer hover:bg-blue-50 px-1 py-0.5 rounded ${!item.doReceiveDate ? 'text-red-600' : ''}`}
                                                                                            title="Click to edit"
                                                                                        >
                                                                                            {formatDate(item.doReceiveDate)}
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-2 py-1 text-xs">
                                                                                    {isEditingWarehouseRcvDate ? (
                                                                                        <input
                                                                                            type="date"
                                                                                            value={editItemValue}
                                                                                            onChange={(e) => setEditItemValue(e.target.value)}
                                                                                            onBlur={() => handleItemEditSave(order._id, itemIndex, 'warehouseRcvDate')}
                                                                                            onKeyPress={(e) => {
                                                                                                if (e.key === 'Enter') {
                                                                                                    handleItemEditSave(order._id, itemIndex, 'warehouseRcvDate');
                                                                                                } else if (e.key === 'Escape') {
                                                                                                    handleItemEditCancel();
                                                                                                }
                                                                                            }}
                                                                                            className="w-full px-1 py-0.5 text-xs border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                                            autoFocus
                                                                                        />
                                                                                    ) : (
                                                                                        <div
                                                                                            onClick={() => handleItemEditStart(order._id, itemIndex, 'warehouseRcvDate', item.warehouseRcvDate)}
                                                                                            className={`cursor-pointer hover:bg-blue-50 px-1 py-0.5 rounded ${!item.warehouseRcvDate ? 'text-red-600' : ''}`}
                                                                                            title="Click to edit"
                                                                                        >
                                                                                            {formatDate(item.warehouseRcvDate)}
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-2 py-1 text-xs font-semibold">
                                                                                    {formatSGD(item.sellingPrice)}
                                                                                </td>
                                                                                <td className="px-2 py-1 text-xs">
                                                                                    {formatRMB(item.costRMB)}
                                                                                </td>
                                                                                <td className="px-2 py-1 text-xs font-semibold text-green-600">
                                                                                    {formatSGD(item.itemProfitSGD)}
                                                                                </td>
                                                                                <td className="px-2 py-1 text-xs">
                                                                                    {item.remarks || ''}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        {order.creditNotes && Array.isArray(order.creditNotes) && order.creditNotes.length > 0 && (
                                                            <div className="mt-3 bg-red-50 p-3 rounded-lg overflow-x-auto" style={{ width: '70%' }}>
                                                                <table className="min-w-full divide-y divide-gray-200 text-xs">
                                                                    <thead className="bg-red-100">
                                                                        <tr>
                                                                            <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">Credit Type</th>
                                                                            <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">Amount</th>
                                                                            <th className="px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">Remarks</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="bg-red-50 divide-y divide-gray-200">
                                                                        {(order.creditNotes || []).map((cn, cnIndex) => (
                                                                            <tr key={cnIndex}>
                                                                                <td className="px-2 py-1 text-gray-700">{cn.type === 'aftersalesservice' ? 'After Sales Service' : (cn.type || 'N/A')}</td>
                                                                                <td className="px-2 py-1 text-gray-700">{formatSGD(cn.amountSGD != null ? cn.amountSGD : 0)}</td>
                                                                                <td className="px-2 py-1 text-gray-700">{cn.remarks || 'N/A'}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </div>
        </Layout>
    );
};

export default FOOrderPage;
